import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { LABELS_LANGUAGES } from 'src/app/pages/widget/share/labels.model';
import { ApiUrlService } from 'src/services/api-url.service';
import { NotificationsService } from './notifications.service';
import { normalizeWidgetLang } from './lang.utils';

@Injectable({ providedIn: 'root' })
export class TranslationService {

  public data: any = {};
  public loadingData = new BehaviorSubject(false);

  openAiKey = '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';
  availableLanguages = {
    en: 'inglés',
    ca: 'catalán',
    pt: 'portugués',
    de: 'alemán',
    fr: 'francés',
    it: 'italiano'
  }

  loadersLanguage = {
    en: 'Retrieving data from the request...',
    ca: 'Obtenint dades de la sol·licitud...',
    pt: 'A recuperar dados da solicitação...',
    de: 'Daten aus der Anfrage werden abgerufen …',
    fr: 'Récupération des données de la requête...',
    it: 'Recupero dei dati dalla richiesta...'
  }

  constructor(private http: HttpClient, public notis: NotificationsService, private apiUrlService: ApiUrlService) {

  }

  getLoadingLanguage() {
    return this.loadingData.asObservable();
  }

  use(lang: string): Promise<{}> {
    this.loadingData.next(true);
    return new Promise<{}>(resolve => {
      const langPath = `./assets/lang/${lang || 'en'}.json`;

      this.http.get(langPath).subscribe(
        response => {
          localStorage.setItem('belender-language', lang);
          this.data = response || {};
          this.loadingData.next(false);
          resolve(this.data);
        },
        err => {
          this.data = {};
          resolve(this.data);
        }
      );
    });
  }

  getLabelsTranslated(lang) {
    const normalized = normalizeWidgetLang(lang);
    const labels = LABELS_LANGUAGES[normalized] || LABELS_LANGUAGES.es;
    const result = { ...labels };
    const fallbackLang = normalized === 'es' ? 'es' : (LABELS_LANGUAGES[normalized] ? normalized : 'en');
    const fallback = LABELS_LANGUAGES[fallbackLang] || LABELS_LANGUAGES.es;

    ['certificate', 'flowCommon', 'videoid'].forEach((key) => {
      if (!result[key] && (fallback[key] || LABELS_LANGUAGES.es[key])) {
        result[key] = fallback[key] || LABELS_LANGUAGES.es[key];
      }
    });

    return result;
  }

  /** Resolves configurable action labels without changing source objects. */
  resolveFlowActionLabels(
    flowConfig: any,
    funderConfig: any,
    explicitLanguage?: string | null,
    currentLabels?: any,
    options: {
      baseLabels?: 'translated' | 'current' | 'currentWhenNoExplicitLanguage';
      resolveCertificateAutofirma?: boolean;
      resolveGenericActions?: boolean;
    } = {}
  ) {
    const baseLabels = options.baseLabels === 'current'
      ? currentLabels
      : options.baseLabels === 'currentWhenNoExplicitLanguage' && !explicitLanguage
        ? currentLabels
        : this.getLabelsTranslated(explicitLanguage);

    return this.resolveActionLabels(
      baseLabels,
      flowConfig,
      funderConfig,
      explicitLanguage,
      options
    );
  }

  /** Resolves configurable action labels without changing source objects. */
  resolveActionLabels(
    labels: any,
    flowConfig: any,
    funderConfig: any,
    explicitLanguage?: string | null,
    options: {
      resolveCertificateAutofirma?: boolean;
      resolveGenericActions?: boolean;
    } = {}
  ) {
    const isTranslatedLanguage = this.isNonSpanishLanguage(explicitLanguage);
    const resolveGenericActions = options.resolveGenericActions !== false;
    const valid = (value: any): value is string => typeof value === 'string' && value.trim() !== '';
    const translated = flowConfig?.config_translate?.buttons?.text;
    const funder = funderConfig?.buttons?.text;
    const actions = labels?.actions || {};
    const certificate = labels?.certificate || {};
    const autofirma = certificate.autofirma || {};

    return {
      ...labels,
      ...(resolveGenericActions ? {
        actions: {
          ...actions,
          next: (isTranslatedLanguage && valid(translated?.next) ? translated.next : undefined)
            || (valid(funder?.next) ? funder.next : undefined)
            || actions.next,
          retry: (isTranslatedLanguage && valid(translated?.retry) ? translated.retry : undefined)
            || (valid(funder?.retry) ? funder.retry : undefined)
            || actions.retry,
        },
      } : {}),
      ...(options.resolveCertificateAutofirma ? {
        certificate: {
          ...certificate,
          autofirma: {
            ...autofirma,
            signCertificate: (isTranslatedLanguage && valid(translated?.certificate) ? translated.certificate : undefined)
              || (valid(funder?.certificate) ? funder.certificate : undefined)
              || autofirma.signCertificate,
          },
        },
      } : {}),
    };
  }

  isNonSpanishLanguage(language?: string | null): boolean {
    return typeof language === 'string'
      && language.trim() !== ''
      && !['es', 'sp'].includes(normalizeWidgetLang(language));
  }

  getFirstLoading(lang) {
    const normalized = normalizeWidgetLang(lang);
    if (this.loadersLanguage[normalized]) {
      return this.loadersLanguage[normalized];
    }
    return 'Obteniendo datos de la solicitud...';
  }

  getLanguage(lang) {
    const normalized = normalizeWidgetLang(lang);
    if (this.availableLanguages[normalized]) {
      return this.availableLanguages[normalized];
    }

    return null;
  }

  getLanguageText(lang) {
    const normalized = normalizeWidgetLang(lang);
    if (this.availableLanguages[normalized]) {
      return this.availableLanguages[normalized];
    }

    return lang;
  }

  async getTraductionFromApi(userID, flowID, lang) {
        const params = new URLSearchParams({ user_id: userID, language: lang, flow_name: flowID });
        const url = `${this.apiUrlService.baseUrl}/translate?${params}`;
    
        return new Promise((resolve, reject) => {
          this.http.get(url).subscribe(async (d: any) => {
              if(d && d) {
                resolve(d);
              } else {
                this.notis.showToast('No existe una traducción para el idioma ' + this.getLanguageText(lang), 5000, 'error')
                reject(d);
              }
          }, e => {
              this.notis.showToast('No existe una traducción para el idioma ' + this.getLanguageText(lang), 5000, 'error')
              reject(e)
          }); 
        });
  }

  async getTraduction(obj, lang) {
        let url = 'https://api.openai.com/v1/chat/completions';
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.openAiKey}`
        });
        let languague = this.getLanguage(lang);
        const prompt = `Te doy un objeto JSON. Devuélveme el mismo JSON pero con todos los valores de tipo string traducidos al ${languague}. 
          No modifiques las claves, la estructura ni los tipos (números, booleanos, null deben permanecer iguales). 
          Conserva placeholders como {{name}} exactamente. No cambies etiquetas HTML ni markdown dentro de strings.
          Devuélvelo solamente como JSON válido sin texto adicional, no me pongas ni comillas al principio ni la palabra "json". JSON a traducir: ${JSON.stringify(obj)}`;

        const body = {
        model: "gpt-4.1", // Puedes usar otros modelos como gpt-4 si tienes acceso
        messages: [
            {
            role: "user",
            content:  prompt
            },
        ],
        temperature: 0, // Ajusta la creatividad de la respuesta (0.0 - 1.0)
        };
        return new Promise((resolve, reject) => {
            if(!languague) {
              resolve(null);
            } else {
              this.http.post<any>(url, body, { headers }).subscribe(data => {
                if(data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) {
                  try{
                    let res = JSON.parse(data.choices[0].message.content);
                    console.warn(res);
                    resolve(res);
                  } catch {
                    resolve(null);
                  }
                    
                } else {
                    resolve(null);
                }
                
              }, e => reject(e))
            }
        })
        
        
        
  }
}
